import React, { useEffect } from 'react';
import Smooch from 'smooch';
import useUser from '@hooks/user/use-user';
import Environment from '@config/environment';
import { usePreferences } from '@context/preferences.context';
import logger from '@helpers/utils/logger/client';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { getLocaleByLanguage } from '@helpers/localization';

interface SunCoMessage extends Message {
  avatarUrl?: string;
  metadata?: {
    '__zendesk_msg.agent.id'?: string;
    ticketClosed?: boolean;
  };
}

const SmoochNotification: React.FC = () => {
  const { user } = useUser();

  const { sendAnalyticEvent } = useAnalyticEvents('footer');

  const { language, country } = usePreferences();

  useEffect(() => {
    const vcSunCo = localStorage.getItem('vcSunCo');

    const integrationId = Environment.sunCoIntegrationId;

    if (vcSunCo && integrationId && user?.id) {
      Smooch.on('widget:opened', function () {
        sendAnalyticEvent('contact_us', { category: 'contact_us', action: 'start_chat', label: 'sunco' });
      });

      Smooch.on('widget:closed', function () {
        Smooch.destroy();
      });

      Smooch.init({
        integrationId,
        jwt: vcSunCo,
        externalId: user.id,
        locale: getLocaleByLanguage(language),
        canUserSeeConversationList: false,
        customColors: {
          brandColor: 'ffffff',
          conversationColor: '000',
          actionColor: '000',
        },
        delegate: {
          beforeDisplay(message: SunCoMessage) {
            const type: string = message?.source?.type;
            const hasAgentId = message.metadata && message.metadata['__zendesk_msg.agent.id'];

            if (type === 'api' || (type === 'zd:agentWorkspace' && !hasAgentId)) {
              message.displayName = 'Vestiaire Collective';
              message.avatarUrl = 'https://assets.vestiairecollective.com/images/logos/v_60x60r2x.png';
            }

            if (message.metadata?.ticketClosed) {
              return null;
            }

            return message;
          },
        },
      })
        .then(function () {
          const botMindLanguageMap: Record<string, string> = {
            en: 'english',
            fr: 'french',
            de: 'german',
            it: 'italian',
            es: 'spanish',
          };

          Smooch.updateUser({
            metadata: {
              user_language: botMindLanguageMap[language] || botMindLanguageMap.en,
              user_country: country,
            },
          });

          const conversations = Smooch.getConversations();

          conversations.forEach((item, idx) => {
            if (idx !== 0 && item.participants.length && item.participants[0].unreadCount) {
              Smooch.markAllAsRead(item.id);
            }
          });

          const monoConversation = conversations.length ? conversations[0] : null;

          if (monoConversation && monoConversation.participants.length) {
            const unreadCount = monoConversation.participants[0].unreadCount;

            if (unreadCount) {
              return;
            }
          }
          Smooch.destroy();
        })
        .catch((err) => {
          logger.error(err, 'Init Smooch failed');
        });
    }

    if (!user?.id) {
      Smooch.destroy();
    }
  }, [user?.id]);

  return <></>;
};

export default SmoochNotification;
